import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Field, Formik } from 'formik';

import useTranslation from 'components/hooks/useTranslation';
import Api from 'components/Api';
import UiMsg from 'components/ui/UiMsg';
import BngForm from 'components/bng/form/BngForm';
import BngField from 'components/bng/form/BngField';
import { BngCancelButton, BngPrimaryButton } from 'components/ui/Button';
import bngYup from 'components/bng/form/yup/BngYup';
import RequestProjectAccessDialog from 'bng/pages/login/RequestProjectAccessDialog';

const FormSchema = bngYup((yup) =>
  yup.object({
    email: yup.string().email().required().ensure().default(''),
  })
);

export default function LoginRegisterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const state = useMemo(() => _.cloneDeep(locationState), []);
  const { loadingCallback } = useOutletContext();

  const [requestAccessDialogProps, setRequestAccessDialogProps] = useState(null);

  return (
    <Formik
      initialValues={{ email: state?.credentials?.email ?? '' }}
      validationSchema={FormSchema}
      onSubmit={async (values) => {
        loadingCallback(true);
        try {
          const dialogProps = { userEmail: values.email ?? '' };
          const urlDomain = window.location.host.split('.')[0];
          const urlProjects = await Api.Project.findProjectsByTrustedDomain(urlDomain);

          if (urlProjects.length > 0) {
            dialogProps.projectsThatTrustDomain = urlProjects;
            dialogProps.projectToRequestFromUrl = urlProjects;
            setRequestAccessDialogProps(dialogProps);
          } else {
            const domain = '@' + values.email.split('@')[1];
            const user = await Api.User.findUserForRegister(values.email);

            if (user) {
              if (!user.enabled) {
                const invites = await Api.ProjectInvite.resendInvite(user.id, values.email);
                if (invites.length > 0) {
                  invites.forEach((invite) => UiMsg.ok(t('resend.existing.invite', invite.project.displayName)))
                  return;
                }
              } else {
                UiMsg.error(t('email_alredy_in_use'));
                return;
              }
            }

            dialogProps.projectsThatTrustDomain = await Api.Project.findProjectsByTrustedDomain(domain);

            if (dialogProps.projectsThatTrustDomain.length > 0) {
              setRequestAccessDialogProps(dialogProps);
            } else {
              const url = Api.buildUrl(
                'https://register.bimachine.com',
                {
                  origin: 'plataforma',
                  suborigin: 'applogin',
                  lng: 'pt',
                  lead: values.email,
                }
              );
              window.location.replace(url);
            }
          }
        } catch (e) {
          console.error('Error while registering', { values }, e);
          UiMsg.ajaxError(null, e);
        } finally {
          loadingCallback(false);
        }
      }}
    >
      <BngForm>
        <label className="mb-0 fw-400 text-base">{t('login.register.label')}</label>
        <Field component={BngField} name="email" rootClassName="mb-0 mt-2" floatingLabel autoFocus />

        <div className="flex-center-items jc-end mt-4">
          <BngCancelButton
            className={'fw-500'}
            onClick={() => {
              navigate('/login');
            }}
          >
            {t('login_back')}
          </BngCancelButton>
        </div>

        <BngPrimaryButton type="submit" className="mt-3">
          {t('login.register.button')}
        </BngPrimaryButton>

        {requestAccessDialogProps && (
          <RequestProjectAccessDialog
            closeModal={() => setRequestAccessDialogProps(null)}
            {...requestAccessDialogProps}
          />
        )}
      </BngForm>
    </Formik>
  );
}
